import React from 'react';
import { Box, List, ListItemText, ListItemButton, Chip, Typography } from '@mui/material';
import EmptyState from '../../common/EmptyState';
import {
  getTemplateTypeConstant,
  getTemplateTypeText,
  getTemplateTypeColor,
} from '../../../constants/matrixTemplateTypes';

/**
 * Shared "Select Skills Matrix" card list.
 *
 * Extracted from the feedback-session setup flow (TeamStructureView) so the
 * ADR-003 admin "Change skills matrix" action on the report can present the
 * same rich cards (name, position, review-type/version chips, role chips)
 * instead of a plain dropdown. The list itself does no filtering — callers
 * decide which matrices to pass in.
 *
 * @param {Array}  matrices          Matrices to render.
 * @param {Function} onSelect        Called with the matrix when a card is clicked.
 * @param {string} [selectedMatrixId] Highlights the matching card (select-then-confirm flows).
 * @param {Function} [getMatrixRoles] Returns [{ type, version }] for a matrix. Defaults to
 *                                     deriving them from `matrix.templates`.
 * @param {string} [emptyTitle]      Empty-state title.
 * @param {string} [emptyDescription] Empty-state description.
 */
const defaultGetMatrixRoles = (matrix) => {
  const rolesMap = new Map();
  (matrix?.templates || []).forEach((template) => {
    if (template.type) {
      const key = `${template.type}-${template.version || 'no-version'}`;
      if (!rolesMap.has(key)) {
        rolesMap.set(key, { type: template.type, version: template.version });
      }
    }
  });
  return Array.from(rolesMap.values());
};

const SkillsMatrixSelectionList = ({
  matrices = [],
  onSelect,
  selectedMatrixId,
  getMatrixRoles = defaultGetMatrixRoles,
  emptyTitle = 'No Skills Matrices Available',
  emptyDescription = 'There are no skills matrices available to choose from.',
}) => {
  if (matrices.length === 0) {
    return <EmptyState title={emptyTitle} description={emptyDescription} />;
  }

  return (
    <List sx={{ p: 0 }}>
      {matrices.map((matrix, index) => {
        const isSelected = selectedMatrixId === matrix.id;
        return (
          <Box
            key={matrix.id}
            sx={{
              mb: index < matrices.length - 1 ? 2 : 0,
              border: '1px solid',
              borderColor: isSelected ? '#2563EB' : '#E2E8F0',
              borderRadius: 1,
              overflow: 'hidden',
              transition: 'all 0.2s ease',
              backgroundColor: isSelected ? '#EFF6FF' : 'transparent',
              '&:hover': {
                borderColor: isSelected ? '#2563EB' : '#CBD5E1',
                boxShadow: '0 2px 4px rgba(0,0,0,0.05)',
                backgroundColor: isSelected ? '#EFF6FF' : '#F8FAFC',
              },
            }}
          >
            <ListItemButton
              selected={isSelected}
              onClick={() => onSelect?.(matrix)}
              sx={{
                p: 2,
                '&:hover': {
                  backgroundColor: 'transparent',
                },
                '&.Mui-selected, &.Mui-selected:hover': {
                  backgroundColor: 'transparent',
                },
              }}
            >
              <ListItemText
                primary={
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="subtitle1" component="span" sx={{ fontWeight: 500, color: '#1E293B' }}>
                      {matrix.name}
                    </Typography>
                    {matrix.position && (
                      <Chip
                        label={matrix.position.label}
                        size="small"
                        sx={{
                          height: '22px',
                          backgroundColor: '#EFF6FF',
                          color: '#2563EB',
                          fontWeight: 500,
                          fontSize: '11px',
                          '& .MuiChip-label': {
                            px: 1,
                          },
                        }}
                      />
                    )}
                  </Box>
                }
                secondary={
                  <Typography variant="body2" component="div">
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 1 }}>
                      {getMatrixRoles(matrix).map((role, i) => (
                        <Typography
                          key={`${role.type}-${role.version || 'no-version'}`}
                          variant="body2"
                          component="span"
                          sx={{
                            color: getTemplateTypeColor(getTemplateTypeConstant(role.type)),
                            fontSize: '11px',
                            fontWeight: 500,
                            display: 'flex',
                            alignItems: 'center',
                          }}
                        >
                          {getTemplateTypeText(getTemplateTypeConstant(role.type))}
                          {role.version && (
                            <Chip
                              label={`v.${role.version}`}
                              size="small"
                              sx={{
                                height: '18px',
                                ml: 0.5,
                                backgroundColor: '#F1F5F9',
                                color: '#64748B',
                                fontSize: '10px',
                                '& .MuiChip-label': {
                                  px: 0.5,
                                },
                              }}
                            />
                          )}
                          {i < getMatrixRoles(matrix).length - 1 && (
                            <Box
                              component="span"
                              sx={{
                                width: '3px',
                                height: '3px',
                                borderRadius: '50%',
                                backgroundColor: '#94A3B8',
                                mx: 1,
                              }}
                            />
                          )}
                        </Typography>
                      ))}
                    </Box>
                    {matrix.matrixRoles && matrix.matrixRoles.length > 0 && (
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 1 }}>
                        {matrix.matrixRoles.map((matrixRole) => (
                          <Chip
                            key={matrixRole.role.id}
                            label={matrixRole.role.label}
                            size="small"
                            sx={{
                              height: '20px',
                              backgroundColor: '#F1F5F9',
                              color: '#64748B',
                              fontSize: '11px',
                              fontStyle: 'italic',
                              '& .MuiChip-label': {
                                px: 1,
                              },
                            }}
                          />
                        ))}
                      </Box>
                    )}
                  </Typography>
                }
              />
            </ListItemButton>
          </Box>
        );
      })}
    </List>
  );
};

export default SkillsMatrixSelectionList;
